import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * ------------------------------------------------------------------
 * INDICE DE FUNCIONES Y ESTRUCTURA
 * ------------------------------------------------------------------
 * 1. Context Creation .................... Creación del contexto
 * 2. Custom Hook (useDrawer) ............. Hook para usar el drawer
 * 3. Provider (DrawerProvider) ........... Componente proveedor
 *    - State ............................. Estados (open, content, title, options)
 *    - openDrawer ........................ Abrir drawer con contenido
 *    - closeDrawer ....................... Cerrar drawer
 *    - setBeforeCloseHandler ............. Configurar handler antes de cerrar
 *    - requestClose ...................... Intentar cerrar (verifica handler)
 * ------------------------------------------------------------------
 */

/**
 * ------------------------------------------------------------------
 * 1. Context Creation
 * ------------------------------------------------------------------
 */
const DrawerContext = createContext();

/**
 * ------------------------------------------------------------------
 * 2. Custom Hook (useDrawer)
 * ------------------------------------------------------------------
 */
export function useDrawer() {
  return useContext(DrawerContext);
}

/**
 * ------------------------------------------------------------------
 * 3. Provider (DrawerProvider)
 * ------------------------------------------------------------------
 */
export function DrawerProvider({ children }) {
  // State
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState(null);
  const [title, setTitle] = useState('');
  const [options, setOptions] = useState({});
  const [beforeClose, setBeforeClose] = useState(null);

  // openDrawer
  const openDrawer = useCallback((drawerContent, drawerTitle = '', drawerOptions = {}) => {
    setContent(drawerContent);
    setTitle(drawerTitle);
    setOptions(drawerOptions);
    setOpen(true);
    // clear any previous beforeClose handler when opening new drawer
    setBeforeClose(null);
  }, []);

  // closeDrawer
  const closeDrawer = useCallback(() => {
    setOpen(false);
    setContent(null);
    setTitle('');
    setBeforeClose(null);
  }, []);

  // setBeforeCloseHandler
  const setBeforeCloseHandler = useCallback((fn) => {
    setBeforeClose(() => fn);
  }, []);

  // requestClose
  const requestClose = useCallback(async () => {
    if (!beforeClose) {
      closeDrawer();
      return true;
    }
    try {
      const result = beforeClose();
      if (result && typeof result.then === 'function') {
        const allowed = await result;
        if (allowed) closeDrawer();
        return !!allowed;
      }
      if (result === false) return false;
      // any other truthy/undefined -> allow close
      closeDrawer();
      return true;
    } catch (e) {
      // in case of error, do not close
      return false;
    }
  }, [beforeClose, closeDrawer]);

  return (
    <DrawerContext.Provider value={{ open, content, title, options, openDrawer, closeDrawer, setBeforeClose: setBeforeCloseHandler, requestClose }}>
      {children}
    </DrawerContext.Provider>
  );
}